// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  IconButton,
  Card,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// ** Icon Imports
import Icon from "components/icon";

// ** Custom Component Imports
import CustomChip from "components/mui/chip";
import dayjs from "dayjs";

const lineItemStyles = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const formatPrice = (val) => {
  if (val == null || val === "") return "N/A";
  const num = typeof val === "number" ? val : Number(val);
  return isNaN(num)
    ? "N/A"
    : `$${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const PaymentReceipt = ({
  open,
  handleClose,
  handleClosePaymentDetail,
  handleCloseRefundDlg,
  entity,
  payment,
  customerData,
  invoiceData,
  fetchPayments,
  invoiceID,
}) => {
  const theme = useTheme();

  const prettyDate = (d) =>
    d ? (typeof d === "string" && d.includes("T") ? dayjs(d).format("MMM D, YYYY") : d) : "";
  
  const DetailContent = () => {
    return (
      <Grid container spacing={5} p={{ xs: 3, sm: 10 }}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            <img src="/images/Logo3.png" height={80} width={80} />
            <Typography variant="h4">Payment Receipt</Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                color="text.primary"
                fontWeight="bold"
                sx={{ 
                  backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#F4F5F6", 
                  px: 3, 
                  py: 1 
                }}
              >
                {/* Show Invoice or Advance/Deposit ref */}
                {invoiceData
                  ? `INV-${invoiceData.invoiceID}`
                  : payment.paymentType === "Deposit"
                  ? `${payment.requestType === "Assessment" ? "AST" : "WO"}-${payment.requestID}`
                  : ""}
              </Typography>
              <CustomChip
                skin="light"
                color={payment.status === "Paid" ? "success" : payment.status === "Refunded" ? "warning" : "info"}
                label={payment.status}
                size="small"
                icon={<Icon icon="tabler:circle-check-filled" />}
              />
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Total Paid Amount
              </Typography>
              <Typography variant="h4" color="main.main">
               {formatPrice(payment.amount)}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ border: "1px solid #E1E4EA", borderRadius: 1 }}>
            <Box sx={{ borderBottom: "1px solid #E1E4EA", p: 4 }}>
              <Typography variant="h6" fontWeight="bold">
                Payment Details
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: 4,
                backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#FAFAFA",
              }}
            >
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                  {invoiceData || payment.paymentType === "Invoice" 
                    ? "Invoice ID" 
                    : "Advance ID"}
                </Typography>
                <Typography variant="body2">
                  {invoiceData
                    ? `INV-${invoiceData.invoiceID}`
                    : payment.paymentType === "Deposit"
                    ? `${payment.requestType === "Assessment" ? "AST" : "WO"}-${payment.requestID}`
                    : payment.invoiceID
                    ? `INV-${payment.invoiceID}`
                    : ""}
                </Typography>
              </Box>
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                {invoiceData || payment.paymentType === "Invoice" 
                    ? "Invoice " 
                    : "Advance "} 
                  Issue Date
                </Typography>
                <Typography variant="body2">
                  {invoiceData && invoiceData.invoiceDate
                    ? prettyDate(invoiceData.invoiceDate)
                    : payment.paymentDate
                    ? prettyDate(payment.paymentDate)
                    : "N/A"}
                </Typography>
              </Box>
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                  Payment Date
                </Typography>
                <Typography variant="body2">
                  {payment.paymentDate ? prettyDate(payment.paymentDate) : "N/A"}
                </Typography>
              </Box>
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                  Payment Method
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, color: "main.main" }}>
                  <Icon icon="tabler:cash-banknote" />
                  <Typography variant="body2">{payment.paymentMethod || "N/A"}</Typography>
                </Box>
              </Box>
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                  Reference Number
                </Typography>
                <Typography variant="body2">{payment.PAYpaymentID || "N/A"}</Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ border: "1px solid #E1E4EA", borderRadius: 1 }}>
            <Box sx={{ borderBottom: "1px solid #E1E4EA", p: 4 }}>
              <Typography variant="h6" fontWeight="bold">
                Transaction Details
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: 4,
                backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#FAFAFA",
              }}
            >
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                {invoiceData || payment.paymentType === "Invoice" 
                    ? "Invoice " 
                    : "Advance "} Amount
                </Typography>
                <Typography variant="body2">
                   {formatPrice(invoiceData?.totalAmount ?? payment.amount)}
                </Typography>
              </Box>
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                  Outstanding Amount
                </Typography>
                <Typography variant="body2">
                  {formatPrice(
                    Math.max(
                      (invoiceData?.totalAmount ?? 0) - (Number(invoiceData?.depositUtilizedAmount) ?? 0) - (payment?.amount ?? 0),
                      0
                    )
                  )}
                </Typography>
              </Box>
              <Divider />
              <Box sx={lineItemStyles}>
                <Typography variant="body2" fontWeight="bold">
                  Paid Amount
                </Typography>
                <Typography variant="body2">
                  {formatPrice(payment.amount)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    );
  };

// ** inside PaymentReceipt.js **

const RefundContent = () => {

  if (!payment) return null;

  return (
    <Grid container spacing={5} p={{ xs: 2, sm: 10 }}>
      {/* Header */}
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <img src="/images/Logo3.png" height={80} width={80} />
          <Typography variant="h4">Refund Receipt</Typography>
          <CustomChip
            skin="light"
            color="warning"
            label="Refunded"
            size="small"
            icon={<Icon icon="tabler:circle-check-filled" />}
          />
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Total Amount Refunded
            </Typography>
            <Typography variant="h4" color="warning.main">
              {formatPrice(payment?.amount)}
            </Typography>
          </Box>
        </Box>
      </Grid>

      {/* Refund Information */}
      <Grid item xs={12}>
        <Box sx={{ border: "1px solid #E1E4EA", borderRadius: 1 }}>
          <Box sx={{ borderBottom: "1px solid #E1E4EA", p: 4 }}>
            <Typography variant="h6" fontWeight="bold">
              Refund Information
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              p: 4,
              backgroundColor: "#FAFAFA",
            }}
          >
            <Box sx={lineItemStyles}>
              <Typography variant="body2" fontWeight="bold">
                Refunded Amount
              </Typography>
              <Typography variant="body2">
                 {formatPrice(payment?.amount)}
              </Typography>
            </Box>
            <Box sx={lineItemStyles}>
              <Typography variant="body2" fontWeight="bold">
                Refund Issue Date
              </Typography>
              <Typography variant="body2">
                {payment.paymentDate ? prettyDate(payment.paymentDate) : "N/A"}
              </Typography>
            </Box>
            <Box sx={lineItemStyles}>
              <Typography variant="body2" fontWeight="bold">
                Refunded By
              </Typography>
              <Typography variant="body2">
                 {payment?.paidBy || "N/A"}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Grid>

      {/* Transaction Details */}
      <Grid item xs={12}>
        <Box sx={{ border: "1px solid #E1E4EA", borderRadius: 1 }}>
          <Box sx={{ borderBottom: "1px solid #E1E4EA", p: 4 }}>
            <Typography variant="h6" fontWeight="bold">
              Transaction Details
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              p: 4,
              backgroundColor: "#FAFAFA",
            }}
          >
            <Box sx={lineItemStyles}>
              <Typography variant="body2" fontWeight="bold">
                Invoice ID
              </Typography>
              <Typography variant="body2">
                {invoiceData
                  ? `INV-${invoiceData.invoiceID}`
                  : payment.invoiceID
                  ? `INV-${payment.invoiceID}`
                  : "—"}
              </Typography>
            </Box>
            <Box sx={lineItemStyles}>
              <Typography variant="body2" fontWeight="bold">
                Payment Date
              </Typography>
              <Typography variant="body2">
                {payment.paymentDate ? prettyDate(payment.paymentDate) : "N/A"}
              </Typography>
            </Box>
            <Box sx={lineItemStyles}>
              <Typography variant="body2" fontWeight="bold">
                Payment Method
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Icon icon="tabler:cash-banknote" />
                <Typography variant="body2">
                  {payment?.paymentMethod || "N/A"}
                </Typography>
              </Box>
            </Box>
            <Box sx={lineItemStyles}>
              <Typography variant="body2" fontWeight="bold">
                Reference Number
              </Typography>
              <Typography variant="body2">
                {payment?.paymentID ?? "N/A"}
              </Typography>
            </Box>
            <Divider />
            <Box sx={lineItemStyles}>
              <Typography variant="body2" fontWeight="bold">
                Refunded Amount
              </Typography>
              <Typography variant="body2">
                 {formatPrice(payment?.amount)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};


  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={open}
      onClose={
        () => {
          if(entity === 'detail'){
            handleClose();
            handleClosePaymentDetail();
            fetchPayments(invoiceID);
          }
          if(entity === 'refund'){
            handleClose();
            handleCloseRefundDlg();
            fetchPayments(invoiceID);
          }
        }        
      }
      aria-labelledby="form-dialog-title"
      PaperProps={{
        sx: {
          height: "100%",
          maxHeight: "100vh",
        },
      }}
    >
     <Box sx={{ 
    display: { xs: 'flex', sm: 'none' }, 
    justifyContent: 'center',
    pt: 4
  }}>
    <CustomChip
      skin="light"
      color="info"
      label="Preview Mode"
      size="small"
      icon={<Icon icon="tabler:eye" />}
      sx={{
        height: 20,
        '& .MuiChip-label': { fontSize: '0.65rem', px: 2 },
        '& .MuiChip-icon': { fontSize: '0.7rem', ml: 1 },
        minWidth: 90,
      }}
    />
  </Box>

  {/* Preview Chip - Desktop Position (original placement) */}
  <CustomChip
    skin="light"
    color="info"
    label="Preview Mode"
    size="small"
    icon={<Icon icon="tabler:eye" />}
    sx={{
      display: { xs: 'none', sm: 'flex' },
      position: 'absolute',
      top: 38,
      right: 48,
      zIndex: 8,
      height: 'auto',
      '& .MuiChip-label': { fontSize: '0.8125rem', px: 2 },
      '& .MuiChip-icon': { fontSize: '1rem', ml: 1},
    }}
  />

      <DialogContent>
        <Card>
          {/* {entity === "detail" ? (
            <DetailContent />
          ) : entity === "refund" ? (
            <RefundContent />
          ) : (
            <></>
          )} */}
          {entity === "detail" && <DetailContent />}
          {entity === "refund" && payment && <RefundContent />}
        </Card>
      </DialogContent>
       <DialogActions
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" }, // Stack vertically on mobile
      justifyContent: "space-between",
      gap: 2,
      padding: "16px !important",
      boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
      zIndex: 1,
    }}
    className="dialog-actions-dense"
  >
    {/* Close button - full width on mobile */}
    <Button 
      variant="text" 
      color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
      onClick={() => {
        if(entity === 'detail'){
          handleClose();
          handleClosePaymentDetail();
          fetchPayments(invoiceID);
        }
        if(entity === 'refund'){
          handleClose();
          handleCloseRefundDlg();
          fetchPayments(invoiceID);
        }
      }}
      fullWidth
      sx={{ 
        order: { xs: 1, sm: 0 }, // Make it first in mobile
        textAlign: 'left',
        justifyContent: { xs: 'center', sm: 'center' }
      }}
    >
      Close
    </Button>

    {/* Back to List View - full width on mobile */}
    <Button
      variant="text"
      color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
      onClick={() => {
        if(entity === 'detail'){
          handleClose();
          handleClosePaymentDetail();
          fetchPayments(invoiceID);
        }
        if(entity === 'refund'){
          handleClose();
          handleCloseRefundDlg();
          fetchPayments(invoiceID);
        }
      }}
      fullWidth
      sx={{ 
        order: { xs: 2, sm: 0 },
        textAlign: 'left',
        justifyContent: { xs: 'center', sm: 'center' }
      }}
    >
      Back to List View
    </Button>

    {/* Send Receipt - full width on mobile */}
    <Button 
      variant="contained" 
      color="main" 
      onClick={() => {
        if(entity === 'detail'){
          handleClose();
          handleClosePaymentDetail();
          fetchPayments(invoiceID);
        }
        if(entity === 'refund'){
          handleClose();
          handleCloseRefundDlg();
          fetchPayments(invoiceID);
        }
      }}
      fullWidth
      sx={{ 
        order: { xs: 3, sm: 0 },
        justifyContent: { xs: 'center', sm: 'center' }
      }}
    >
      Send Receipt
    </Button>
  </DialogActions>
    </Dialog>
  );
};

export default PaymentReceipt;
